// Hero5.js - Advanced, unique Hero section for the website builder
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a unique, modern Hero section with a split layout and testimonial callout
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the hero is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The hero elements
 */
export const createHeroElements = (
  getHierarchyName,
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Unique class names
  const heroSectionClass = 'hero5-section';
  const heroSplitBgClass = 'hero5-split-bg';
  const heroContainerClass = 'hero5-container';
  const heroLeftClass = 'hero5-left';
  const heroRightClass = 'hero5-right';
  const heroBadgeClass = 'hero5-badge';
  const heroTitleClass = 'hero5-title';
  const heroGradientTextClass = 'hero5-gradient-text';
  const heroDescClass = 'hero5-desc';
  const heroCtaClass = 'hero5-cta';
  const heroCtaBtnClass = 'hero5-cta-btn';
  const heroCtaSecondaryBtnClass = 'hero5-cta-secondary-btn';
  const heroImageWrapperClass = 'hero5-image-wrapper';
  const heroImageClass = 'hero5-image';
  const heroTestimonialClass = 'hero5-testimonial';
  const heroTestimonialQuoteClass = 'hero5-testimonial-quote';
  const heroTestimonialAvatarClass = 'hero5-testimonial-avatar';
  const heroTestimonialNameClass = 'hero5-testimonial-name';
  const heroTestimonialRoleClass = 'hero5-testimonial-role';

  // Desktop styles
  const heroSectionStyles = {
    position: 'relative',
    width: '100%',
    minHeight: '680px',
    background: 'linear-gradient(120deg, #f0e7ff 0%, #e0f7fa 100%)',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0',
  };

  const heroSplitBgStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    zIndex: 0,
  };

  const heroContainerStyles = {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '60px 32px',
    gap: '48px',
  };

  const heroLeftStyles = {
    flex: '1.1',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
    alignItems: 'flex-start',
    zIndex: 2,
  };

  const heroBadgeStyles = {
    display: 'inline-block',
    background: 'linear-gradient(90deg, #7c3aed 0%, #06b6d4 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: '15px',
    borderRadius: '999px',
    padding: '7px 20px',
    letterSpacing: '0.04em',
    boxShadow: '0 2px 12px rgba(124,58,237,0.10)',
  };

  const heroTitleStyles = {
    fontSize: '54px',
    fontWeight: 800,
    margin: 0,
    color: '#1a1a2e',
    lineHeight: 1.08,
    letterSpacing: '-0.02em',
  };

  const heroGradientTextStyles = {
    background: 'linear-gradient(90deg, #7c3aed 0%, #06b6d4 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 800,
  };

  const heroDescStyles = {
    fontSize: '20px',
    color: '#374151',
    lineHeight: 1.6,
    margin: 0,
    maxWidth: '520px',
  };

  const heroCtaStyles = {
    display: 'flex',
    gap: '18px',
    marginTop: '18px',
  };

  const heroCtaBtnStyles = {
    padding: '16px 36px',
    background: 'linear-gradient(90deg, #7c3aed 0%, #06b6d4 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '14px',
    fontSize: '17px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 8px 24px -6px rgba(124,58,237,0.18)',
    transition: 'all 0.3s',
  };

  const heroCtaSecondaryBtnStyles = {
    padding: '16px 36px',
    background: '#fff',
    color: '#7c3aed',
    border: '2px solid #7c3aed',
    borderRadius: '14px',
    fontSize: '17px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.3s',
  };

  const heroRightStyles = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '32px',
    zIndex: 2,
  };

  const heroImageWrapperStyles = {
    width: '420px',
    height: '420px',
    borderRadius: '32px',
    overflow: 'hidden',
    boxShadow: '0 16px 48px -12px rgba(6,182,212,0.18)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  };

  const heroImageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '32px',
  };

  const heroTestimonialStyles = {
    background: '#fff',
    borderRadius: '18px',
    boxShadow: '0 4px 24px -8px rgba(124,58,237,0.10)',
    padding: '24px 32px',
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
    maxWidth: '380px',
    marginTop: '12px',
  };

  const heroTestimonialAvatarStyles = {
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #7c3aed',
  };

  const heroTestimonialQuoteStyles = {
    fontSize: '16px',
    color: '#374151',
    fontStyle: 'italic',
    marginBottom: '6px',
  };

  const heroTestimonialNameStyles = {
    fontWeight: 700,
    color: '#1a1a2e',
    fontSize: '15px',
    marginBottom: '2px',
  };

  const heroTestimonialRoleStyles = {
    color: '#7c3aed',
    fontSize: '13px',
    fontWeight: 500,
  };

  // Tablet styles
  const tabletHeroContainerStyles = {
    ...heroContainerStyles,
    flexDirection: 'column-reverse',
    gap: '36px',
    padding: '40px 16px',
  };
  const tabletHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '38px',
  };
  const tabletHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    width: '320px',
    height: '320px',
  };

  // Mobile styles
  const mobileHeroContainerStyles = {
    ...heroContainerStyles,
    flexDirection: 'column-reverse',
    gap: '28px',
    padding: '28px 6px',
  };
  const mobileHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '28px',
    textAlign: 'center',
  };
  const mobileHeroLeftStyles = {
    ...heroLeftStyles,
    alignItems: 'center',
    textAlign: 'center',
    gap: '18px',
  };
  const mobileHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    width: '220px',
    height: '220px',
  };
  const mobileHeroTestimonialStyles = {
    ...heroTestimonialStyles,
    padding: '16px 10px',
    maxWidth: '95vw',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  };

  // Register styles
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    // Desktop
    newRegistry[DEVICES.DESKTOP].set(heroSectionClass, heroSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSplitBgClass, heroSplitBgStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContainerClass, heroContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(heroLeftClass, heroLeftStyles);
    newRegistry[DEVICES.DESKTOP].set(heroBadgeClass, heroBadgeStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleClass, heroTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroGradientTextClass, heroGradientTextStyles);
    newRegistry[DEVICES.DESKTOP].set(heroDescClass, heroDescStyles);
    newRegistry[DEVICES.DESKTOP].set(heroCtaClass, heroCtaStyles);
    newRegistry[DEVICES.DESKTOP].set(heroCtaBtnClass, heroCtaBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroCtaSecondaryBtnClass, heroCtaSecondaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroRightClass, heroRightStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageWrapperClass, heroImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageClass, heroImageStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTestimonialClass, heroTestimonialStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTestimonialAvatarClass, heroTestimonialAvatarStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTestimonialQuoteClass, heroTestimonialQuoteStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTestimonialNameClass, heroTestimonialNameStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTestimonialRoleClass, heroTestimonialRoleStyles);
    // Tablet
    newRegistry[DEVICES.TABLET].set(heroContainerClass, tabletHeroContainerStyles);
    newRegistry[DEVICES.TABLET].set(heroTitleClass, tabletHeroTitleStyles);
    newRegistry[DEVICES.TABLET].set(heroImageWrapperClass, tabletHeroImageWrapperStyles);
    // Mobile
    newRegistry[DEVICES.MOBILE].set(heroContainerClass, mobileHeroContainerStyles);
    newRegistry[DEVICES.MOBILE].set(heroTitleClass, mobileHeroTitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroLeftClass, mobileHeroLeftStyles);
    newRegistry[DEVICES.MOBILE].set(heroImageWrapperClass, mobileHeroImageWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(heroTestimonialClass, mobileHeroTestimonialStyles);
    return newRegistry;
  });

  // Register global class names
  setGlobalClassNames(prev => new Set([
    ...prev,
    heroSectionClass,
    heroSplitBgClass,
    heroContainerClass,
    heroLeftClass,
    heroBadgeClass,
    heroTitleClass,
    heroGradientTextClass,
    heroDescClass,
    heroCtaClass,
    heroCtaBtnClass,
    heroCtaSecondaryBtnClass,
    heroRightClass,
    heroImageWrapperClass,
    heroImageClass,
    heroTestimonialClass,
    heroTestimonialAvatarClass,
    heroTestimonialQuoteClass,
    heroTestimonialNameClass,
    heroTestimonialRoleClass
  ]));

  // Generate IDs
  const now = Date.now();
  const heroSectionId = now;
  const splitBgId = now + 1;
  const containerId = now + 2;
  const leftId = now + 3;
  const badgeId = now + 4;
  const titleId = now + 5;
  const gradientTextId = now + 6;
  const descId = now + 7;
  const ctaId = now + 8;
  const ctaBtnId = now + 9;
  const ctaSecondaryBtnId = now + 10;
  const rightId = now + 11;
  const imageWrapperId = now + 12;
  const imageId = now + 13;
  const testimonialId = now + 14;
  const testimonialAvatarId = now + 15;
  const testimonialQuoteId = now + 16;
  const testimonialNameId = now + 17;
  const testimonialRoleId = now + 18;

  const newElements = [
    // Main Section
    {
      id: heroSectionId,
      builderId: `builder-${now}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Hero Section 5',
      parentId: null,
      className: heroSectionClass,
      styles: {},
      children: [splitBgId, containerId],
      text: '',
      attributes: {
        'data-element': 'hero-section',
        'data-component': 'section',
        'data-section-type': 'hero',
        'data-page-id': currentPageId
      }
    },
    // Split background (for visual effect)
    {
      id: splitBgId,
      builderId: `builder-${now + 1}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Split Background',
      parentId: heroSectionId,
      className: heroSplitBgClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'hero-split-bg'
      }
    },
    // Main container
    {
      id: containerId,
      builderId: `builder-${now + 2}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Container',
      parentId: heroSectionId,
      className: heroContainerClass,
      styles: {},
      children: [leftId, rightId],
      text: '',
      attributes: {
        'data-element': 'hero-container'
      }
    },
    // Left content
    {
      id: leftId,
      builderId: `builder-${now + 3}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Left Content',
      parentId: containerId,
      className: heroLeftClass,
      styles: {},
      children: [badgeId, titleId, descId, ctaId],
      text: '',
      attributes: {
        'data-element': 'hero-left'
      }
    },
    // Badge
    {
      id: badgeId,
      builderId: `builder-${now + 4}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Hero Badge',
      parentId: leftId,
      className: heroBadgeClass,
      styles: {},
      children: [],
      text: 'NEW: AI-Powered Builder',
      attributes: {
        'data-element': 'hero-badge'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${now + 5}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Hero Title',
      parentId: leftId,
      className: heroTitleClass,
      styles: {},
      children: [gradientTextId],
      text: 'Design your dream website with ',
      attributes: {
        'data-element': 'hero-title'
      },
      interactions: {
        events: [{
          id: now + 100,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '0.9s',
            delay: '0.1s',
            iterations: '1'
          }
        }]
      }
    },
    // Gradient Text
    {
      id: gradientTextId,
      builderId: `builder-${now + 6}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Gradient Text',
      parentId: titleId,
      className: heroGradientTextClass,
      styles: {},
      children: [],
      text: 'AI & Drag-and-Drop',
      attributes: {
        'data-element': 'hero-gradient-text'
      }
    },
    // Description
    {
      id: descId,
      builderId: `builder-${now + 7}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Hero Description',
      parentId: leftId,
      className: heroDescClass,
      styles: {},
      children: [],
      text: 'Instantly generate, customize, and launch stunning sites. Experience the next-gen builder with smart suggestions, real-time preview, and effortless editing.',
      attributes: {
        'data-element': 'hero-desc'
      }
    },
    // CTA Buttons
    {
      id: ctaId,
      builderId: `builder-${now + 8}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero CTA',
      parentId: leftId,
      className: heroCtaClass,
      styles: {},
      children: [ctaBtnId, ctaSecondaryBtnId],
      text: '',
      attributes: {
        'data-element': 'hero-cta'
      }
    },
    // CTA Primary
    {
      id: ctaBtnId,
      builderId: `builder-${now + 9}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'CTA Button',
      parentId: ctaId,
      className: heroCtaBtnClass,
      styles: {},
      children: [],
      text: 'Start For Free',
      attributes: {
        'data-element': 'hero-cta-btn'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      interactions: {
        events: [{
          id: now + 101,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-up',
            duration: '0.8s',
            delay: '0.2s',
            iterations: '1'
          }
        }]
      }
    },
    // CTA Secondary
    {
      id: ctaSecondaryBtnId,
      builderId: `builder-${now + 10}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'CTA Secondary Button',
      parentId: ctaId,
      className: heroCtaSecondaryBtnClass,
      styles: {},
      children: [],
      text: 'See How It Works',
      attributes: {
        'data-element': 'hero-cta-secondary-btn'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      interactions: {
        events: [{
          id: now + 102,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-up',
            duration: '0.8s',
            delay: '0.3s',
            iterations: '1'
          }
        }]
      }
    },
    // Right content (image + testimonial)
    {
      id: rightId,
      builderId: `builder-${now + 11}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Right Content',
      parentId: containerId,
      className: heroRightClass,
      styles: {},
      children: [imageWrapperId, testimonialId],
      text: '',
      attributes: {
        'data-element': 'hero-right'
      }
    },
    // Image Wrapper
    {
      id: imageWrapperId,
      builderId: `builder-${now + 12}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Image Wrapper',
      parentId: rightId,
      className: heroImageWrapperClass,
      styles: {},
      children: [imageId],
      text: '',
      attributes: {
        'data-element': 'hero-image-wrapper'
      }
    },
    // Image
    {
      id: imageId,
      builderId: `builder-${now + 13}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Hero Image',
      parentId: imageWrapperId,
      className: heroImageClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'hero-image',
        'alt': 'Website builder preview'
      }
    },
    // Testimonial Card
    {
      id: testimonialId,
      builderId: `builder-${now + 14}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonial',
      parentId: rightId,
      className: heroTestimonialClass,
      styles: {},
      children: [testimonialAvatarId, testimonialQuoteId, testimonialNameId, testimonialRoleId],
      text: '',
      attributes: {
        'data-element': 'hero-testimonial'
      }
    },
    // Testimonial Avatar
    {
      id: testimonialAvatarId,
      builderId: `builder-${now + 15}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Testimonial Avatar',
      parentId: testimonialId,
      className: heroTestimonialAvatarClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://randomuser.me/api/portraits/men/32.jpg',
      attributes: {
        'data-element': 'hero-testimonial-avatar',
        'alt': 'User photo'
      }
    },
    // Testimonial Quote
    {
      id: testimonialQuoteId,
      builderId: `builder-${now + 16}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Testimonial Quote',
      parentId: testimonialId,
      className: heroTestimonialQuoteClass,
      styles: {},
      children: [],
      text: '“The AI builder is a game changer. I created my site in under 10 minutes!”',
      attributes: {
        'data-element': 'hero-testimonial-quote'
      }
    },
    // Testimonial Name
    {
      id: testimonialNameId,
      builderId: `builder-${now + 17}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Testimonial Name',
      parentId: testimonialId,
      className: heroTestimonialNameClass,
      styles: {},
      children: [],
      text: 'Alex Carter',
      attributes: {
        'data-element': 'hero-testimonial-name'
      }
    },
    // Testimonial Role
    {
      id: testimonialRoleId,
      builderId: `builder-${now + 18}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Testimonial Role',
      parentId: testimonialId,
      className: heroTestimonialRoleClass,
      styles: {},
      children: [],
      text: 'Startup Founder',
      attributes: {
        'data-element': 'hero-testimonial-role'
      }
    },
  ];

  return {
    heroSection: newElements[0],
    elements: newElements
  };
};

export default createHeroElements; 